'use client'
import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { Button } from '@/components/ui/button'
import useUserStore from '@/lib/hooks/zustand-stores/use-user-store'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle } from 'lucide-react'
import Image from 'next/image'
import { toast } from 'sonner'

interface SuccessProps {
  onComplete: () => void
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.3 }
  },
  exit: { 
    opacity: 0,
    scale: 0.98,
    transition: { duration: 0.2 }
  }
}

const contentVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { 
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }
  },
  exit: { 
    opacity: 0,
    y: 8,
    transition: { duration: 0.2 }
  }
}

const checkmarkVariants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 25,
      delay: 0.1
    }
  }
}

const SuccessInner: React.FC<SuccessProps> = ({ onComplete }) => {
  const handleContinue = (e: React.MouseEvent) => {
    e.preventDefault()
    onComplete()
  }

  return (
    <AnimatePresence mode="wait">
      <div className="fixed inset-0 flex items-center justify-center z-50">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/30 backdrop-blur-[1px]"
        />
        
        {/* Main content */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="relative w-full max-w-[320px] mx-4 z-10"
        >
          <motion.div
            variants={contentVariants}
            className="bg-white dark:bg-mixus-dark-charcoal/95 p-8 rounded-2xl shadow-xl ring-1 ring-black/5 dark:ring-white/10"
          >
            {/* Success icon */}
            <div className="flex justify-center mb-7">
              <motion.div
                variants={checkmarkVariants}
                className="relative"
              >
                <div className="bg-green-500 rounded-full p-3 shadow-lg shadow-green-500/20">
                  <CheckCircle className="w-7 h-7 text-white" strokeWidth={2.5} />
                </div>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    delay: 0.15,
                    duration: 0.4
                  }}
                  className="absolute inset-0 bg-green-500/20 rounded-full blur-xl -z-10"
                />
              </motion.div>
            </div>
            
            {/* Text content */}
            <div className="text-center space-y-2.5">
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
                className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight"
              >
                Payment Confirmed
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="text-gray-500 dark:text-gray-400 text-[15px] leading-relaxed"
              >
                Thank you for your payment. Your organization has been upgraded.
              </motion.p>
            </div>

            {/* Button */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.4 }}
              className="mt-7"
            >
              <Button
                onClick={handleContinue}
                className="w-full bg-black hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 dark:text-black text-white py-6 rounded-xl transition-all duration-200 font-medium text-[15px] hover:shadow-lg hover:shadow-black/5 dark:hover:shadow-white/5"
              >
                <span className="flex items-center justify-center gap-2">
                  Continue
                  <svg
                    className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </span>
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

const SuccessPopup: React.FC = () => {
  const searchParams = useSearchParams()
  const searchParam = useMemo(
    () => searchParams?.get('payment-success') || '',
    [searchParams]
  )

  const [showSuccess, setShowSuccess] = useState(false)
  const refreshSubscriptionData = useUserStore(state => state.refreshAiUsageAndSubscriptionData)
  const currentUserId = useUserStore(state => state.user?._id)

  const handleSuccessComplete = () => {
    setShowSuccess(false)
    toast.success('Your upgrade is complete!', {
      description: 'Enjoy your enhanced mixus experience'
    })
  }

  useEffect(() => {
    // Simply check for the URL parameter and show the success popup
    if (searchParam === 'true' && currentUserId) {
      // Still refresh subscription data in the background for state consistency
      refreshSubscriptionData(currentUserId).catch(error => {
        console.error('Error refreshing subscription data:', error)
      })
      
      // Show success popup immediately based on URL param
      setShowSuccess(true)
    }
  }, [searchParam, currentUserId, refreshSubscriptionData])

  if (!showSuccess) {
    return null
  }

  return <SuccessInner onComplete={handleSuccessComplete} />
}

export default SuccessPopup
